import ScrollModal from "@/components/common/scroll-modal";
import {
  filterTypesEnum,
  type FilterDateType,
  type FilterOption,
  type FilterSearchType,
  type FilterStatusType,
  type CatalogSearchItem,
  type FilterHierarchyStatusType,
  type FilterStatus,
} from "@/types/common/filters";
import type { UserType } from "@/types/entities/users";
import { formatToYMD, parseLocalDateFromString } from "@/utils/dates";
import { deepEqual } from "@/utils/filters";
import { Button, Heading, Icon } from "@elaniintc/goes-design-system";
import { PageTabs } from "@/components/ui/page-tabs";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import FilterDateRange from "./FilterDateRange";
import FilterSearch from "./FilterSearch";
import FiltersSidebar from "./FiltersSidebar";
import FilterState from "./FilterStatus";
import type { RoleType } from "@/types/entities/roles";
import FilterBasicSearch from "./FilterBasicSearch";
import type { TagType } from "@/types/entities/tags";
import { cn } from "@/utils/theme";
import FilterHierarchyStatus from "./FilterHierarchyStatus";

type HierarchyStatusEntry = {
  label: string;
  value: boolean;
  field: string;
  children: FilterStatus[];
};

interface FiltersProps {
  isOpen: boolean;
  onToggle: (type: string) => void;
  items: FilterOption[];
  onApply: (newOptions: FilterOption[]) => void;
  hasCurrentFilters: boolean;
}

export const FiltersModal = ({
  isOpen,
  onToggle,
  items,
  onApply,
  hasCurrentFilters,
}: FiltersProps) => {
  const [filterItems, setFilterItems] = useState<FilterOption[]>(items);

  const [selectedFilter, setSelectedFilter] = useState<FilterOption>(
    filterItems[0],
  );
  const [isDifferent, setIsDifferent] = useState(false);

  useEffect(() => {
    if (filterItems.some((filter) => filter.isUpdated === true)) {
      if (!isDifferent) setIsDifferent(true);
    } else if (isDifferent) setIsDifferent(false);
  }, [filterItems, isDifferent]);

  const handleUpdateFilters = (newFilter: FilterOption) => {
    const updatedFilters = filterItems.filter(
      (item) => item.id !== newFilter.id,
    );
    const filterToUpdate = items.find((item) => item.id === newFilter.id);
    if (!filterToUpdate) return;

    const areEqual = deepEqual<FilterOption>(
      { ...filterToUpdate, isUpdated: false },
      { ...newFilter, isUpdated: false },
    );
    newFilter = { ...newFilter, isUpdated: !areEqual };
    const sortFilters = [...updatedFilters, newFilter].sort(
      (a, b) => a.id - b.id,
    );

    setFilterItems(sortFilters);
    setSelectedFilter(newFilter);
  };

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={() => onToggle("close")}
      modalContentClassName="max-w-[340px] tablet:max-w-[640px] desktop:max-w-[764px] h-[616px] w-full p-0 gap-0"
      modalHeaderClassName="bg-neutral-foreground"
      modalFooterClassName="bg-neutral-foreground"
      header={
        <div className="flex items-center justify-between p-4">
          <Heading variant="h5" className="mb-0!">
            Filtros
          </Heading>
          <Button
            type="button"
            onClick={() => onToggle("close")}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" className="text-sm text-gray-500" />
          </Button>
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-between p-4">
          <Button
            type="button"
            onClick={() => onToggle("clean")}
            variant="quiet"
            aria-label="Limpiar"
            disabled={!hasCurrentFilters}
            className="cursor-pointer min-w-10 text-base font-semibold leading-3xl font-geist"
          >
            Limpiar filtros
          </Button>
          <div className="flex space-x-3">
            <Button
              type="button"
              onClick={() => onToggle("close")}
              variant="quiet"
              aria-label="Cancelar"
              className="cursor-pointer min-w-10 text-base font-semibold leading-3xl font-geist"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => onApply(filterItems)}
              variant="default"
              aria-label="Applicar"
              className="cursor-pointer min-w-10 text-base font-semibold leading-3xl font-geist"
              disabled={!isDifferent}
            >
              Aplicar
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex tablet:justify-between flex-col tablet:flex-row overflow-hidden flex-1 justify-start">
        <div
          className={cn(
            "hidden tablet:flex tablet:flex-col tablet:w-60 laptop:w-90 laptop:mi n-h-112 border-r-[0.5px] border-neutral-silent",
          )}
        >
          {filterItems.map((filter) => {
            return (
              <FiltersSidebar
                label={filter.label}
                active={filter.id === selectedFilter.id}
                key={filter.id}
                isUpdated={filter.isUpdated || filter.result !== ""}
                onSelected={() => setSelectedFilter(filter)}
              />
            );
          })}
        </div>
        <div className="tablet:hidden flex overflow-x-auto">
          <PageTabs
            activeKey={selectedFilter.id.toString()}
            onTabChange={(key) => {
              const filter = filterItems.find(
                (item) => item.id.toString() === key,
              );
              if (filter) setSelectedFilter(filter);
            }}
            tabs={filterItems.map(({ label, id }) => {
              return { label, key: id.toString() };
            })}
            className="w-full min-w-max"
          />
        </div>
        <div className="w-full tablet:w-101">
          <FiltersDisplay
            item={selectedFilter}
            onUpdateFilters={handleUpdateFilters}
          />
        </div>
      </div>
    </ScrollModal>
  );
};

const FiltersDisplay = ({
  item,
  onUpdateFilters,
}: {
  item: FilterOption;
  onUpdateFilters: (value: FilterOption) => void;
}) => {
  const handleUpdateState = (value: string) => {
    let newFilters = item as FilterStatusType;
    const updatedStatus = newFilters.status.map((state) => {
      if (state.label === value) {
        return { ...state, value: !state.value };
      }
      return state;
    });

    const selectedFields = updatedStatus
      .filter((state) => state.value)
      .map((state) => state.field);

    let newResult = "";
    switch (value) {
      case "all":
        newFilters = {
          ...newFilters,
          result: newFilters.status.map((status) => status.field).join(","),
          status: newFilters.status.map((state) => ({
            ...state,
            value: true,
          })),
        };
        break;
      case "none":
        newFilters = {
          ...newFilters,
          result: "",
          status: newFilters.status.map((state) => ({
            ...state,
            value: false,
          })),
        };
        break;
      default:
        newResult = selectedFields.join(",");
        newFilters = {
          ...newFilters,
          status: updatedStatus,
          result: newResult,
        };
        break;
    }
    onUpdateFilters({ ...item, ...newFilters });
  };

  const buildDesdeResult = (
    newDate: Date | undefined,
    currentResult: string,
    allDates: string[],
  ): string => {
    if (!newDate) return "";
    const formatted = formatToYMD(newDate);
    if (currentResult && allDates.length > 1)
      return formatted + "," + allDates[1];
    return formatted;
  };

  const buildHastaResult = (
    newDate: Date | undefined,
    allDates: string[],
  ): string | null => {
    if (!newDate && allDates.length > 1) return allDates[0];
    if (!newDate) return null;
    if (dayjs(newDate).isBefore(dayjs(allDates[0]))) return null;
    return allDates[0] + "," + formatToYMD(newDate);
  };

  const buildRangeResult = (
    newDate: Date | undefined,
    toDate: Date | undefined,
  ): string | null => {
    if (!newDate) return null;
    if (toDate) return formatToYMD(newDate) + "," + formatToYMD(toDate);
    return formatToYMD(newDate);
  };

  const handleUpdateDate = (
    type: string,
    newDate: Date | undefined,
    toDate?: Date | undefined,
  ) => {
    const currentFilter = item as FilterDateType;
    const allDates = currentFilter.result.split(",");
    let result: string | null = null;

    switch (type) {
      case "desde":
        result = buildDesdeResult(newDate, currentFilter.result, allDates);
        break;
      case "hasta":
        result = buildHastaResult(newDate, allDates);
        break;
      case "range":
        result = buildRangeResult(newDate, toDate);
        break;
      default:
        break;
    }

    if (result !== null) {
      onUpdateFilters({ ...currentFilter, result });
    }
  };

  const addToSearchFilter = (
    filter: FilterSearchType,
    entry: { id: string },
  ): FilterSearchType => {
    const selectIds = filter.result ? filter.result + "," + entry.id : entry.id;
    const selected = filter.result
      ? [...(filter.selected as { id: string }[]), entry]
      : [entry];
    return { ...filter, result: selectIds, selected };
  };

  const removeFromSearchFilter = (
    filter: FilterSearchType,
    entry: { id: string },
  ): FilterSearchType => {
    const selectIds = filter.result
      .split(",")
      .filter((id) => id !== entry.id)
      .join(",");
    const selected = (filter.selected as { id: string }[]).filter(
      (item) => item.id !== entry.id,
    );
    return { ...filter, result: selectIds, selected };
  };

  const handleUpdateSearch = (
    type: "remove" | "add",
    _value: UserType | RoleType | TagType | CatalogSearchItem,
  ) => {
    const currentFilter = item as FilterSearchType;
    if (!_value) return;

    const updated =
      type === "add"
        ? addToSearchFilter(currentFilter, _value)
        : removeFromSearchFilter(currentFilter, _value);

    onUpdateFilters({ ...updated });
  };

  const handleUpdateSearchFromResult = (
    newItems: UserType[] | RoleType[] | CatalogSearchItem[],
  ) => {
    const currentFilter = item as FilterSearchType;
    const selectIds = newItems.map((entry) => entry.id).join(",");
    onUpdateFilters({
      ...currentFilter,
      result: selectIds,
      selected: newItems,
    });
  };

  const handleBasicSearch = (value: string) => {
    onUpdateFilters({ ...item, result: value });
  };

  const toggleParentWithChildren = (
    parent: HierarchyStatusEntry,
  ): HierarchyStatusEntry => {
    const nextValue = !parent.value;
    return {
      ...parent,
      value: nextValue,
      children: parent.children?.map((c) => ({ ...c, value: nextValue })),
    };
  };

  const toggleChildInParent = (
    parent: HierarchyStatusEntry,
    childLabel: string,
  ): HierarchyStatusEntry => {
    const updatedChildren = parent.children?.map((child) =>
      child.label === childLabel ? { ...child, value: !child.value } : child,
    );
    const parentValue = updatedChildren
      ? updatedChildren.every((c) => c.value)
      : parent.value;
    return { ...parent, value: parentValue, children: updatedChildren };
  };

  const updateStatusEntry = (
    parent: HierarchyStatusEntry,
    value: string,
    parentLabel?: string,
  ): HierarchyStatusEntry => {
    if (parent.label === value && !parentLabel) {
      return toggleParentWithChildren(parent);
    }
    const shouldUpdate = parentLabel ? parent.label === parentLabel : true;
    if (!shouldUpdate) return parent;
    return toggleChildInParent(parent, value);
  };

  const handleHierarchyStatus = (value: string, parentLabel?: string) => {
    const currentFilter = item as FilterHierarchyStatusType;

    const updatedGroups = currentFilter.groups.map((group) => ({
      ...group,
      status: group.status.map((parent) =>
        updateStatusEntry(parent, value, parentLabel),
      ),
    }));

    onUpdateFilters({
      ...currentFilter,
      isUpdated: true,
      groups: updatedGroups,
    });
  };

  if (item.type === filterTypesEnum.status) {
    return (
      <FilterState states={item.status} onStatesChange={handleUpdateState} />
    );
  }

  if (item.type === filterTypesEnum.dates) {
    const dates = item.result ? item.result.split(",") : [undefined, undefined];
    return (
      <FilterDateRange
        initialDate={dates[0] ? parseLocalDateFromString(dates[0]) : undefined}
        endDate={dates[1] ? parseLocalDateFromString(dates[1]) : undefined}
        title={item.label}
        shouldDisabledFutureDates={item.shouldDisabledFutureDates}
        onDatesChange={handleUpdateDate}
      />
    );
  }

  if (item.type === filterTypesEnum.textsearch && item.query) {
    return (
      <FilterSearch
        initialSearchText=""
        searchType={item.searchType}
        searchField={item.searchField}
        title={item.label}
        result={item.result}
        useQueryHook={item.query}
        onUpdateSearch={handleUpdateSearch}
        selected={item.selected || []}
        onUpdateSearchFromResult={handleUpdateSearchFromResult}
      />
    );
  }

  if (item.type === "basicsearch") {
    return <FilterBasicSearch onChange={handleBasicSearch} />;
  }

  if (item.type === filterTypesEnum.hierarchyStatus) {
    return (
      <FilterHierarchyStatus
        groups={item.groups}
        filterLabel={item.label}
        onStatesChange={handleHierarchyStatus}
      />
    );
  }
};
